import { useEffect, useState } from "react";
import { API } from "../../App";
import AdminLayout from "../../components/AdminLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Plus, Pencil, Trash2, Percent, X, Loader2, Search, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";

export default function AdminTaxes() {
  const [taxGroups, setTaxGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const [filters, setFilters] = useState({
    name: "",
    status: "all",
  });

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    taxes: [],
    is_active: true,
  });

  useEffect(() => {
    fetchTaxGroups();
  }, []);

  const fetchTaxGroups = async () => {
    try {
      const response = await fetch(`${API}/tax-groups`, { credentials: "include" });
      if (response.ok) {
        setTaxGroups(await response.json());
      }
    } catch (error) {
      console.error("Failed to fetch tax groups:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);

    try {
      const url = editingGroup
        ? `${API}/tax-groups/${editingGroup.group_id}`
        : `${API}/tax-groups`;
      const method = editingGroup ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(formData),
      });

      if (!response.ok) throw new Error("Failed to save tax group");

      toast.success(editingGroup ? "Tax group updated!" : "Tax group created!");
      setIsDialogOpen(false);
      resetForm();
      fetchTaxGroups();
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Failed to save tax group");
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (groupId) => {
    if (!confirm("Are you sure you want to delete this tax group?")) return;

    try {
      const response = await fetch(`${API}/tax-groups/${groupId}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (!response.ok) throw new Error("Failed to delete");

      toast.success("Tax group deleted!");
      fetchTaxGroups();
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete tax group");
    }
  };

  const openEditDialog = (group) => {
    setEditingGroup(group);
    setFormData({
      name: group.name,
      description: group.description || "",
      taxes: group.taxes || [],
      is_active: group.is_active,
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingGroup(null);
    setFormData({
      name: "",
      description: "",
      taxes: [],
      is_active: true,
    });
  };

  const addTax = () => {
    setFormData((prev) => ({
      ...prev,
      taxes: [
        ...prev.taxes,
        { tax_id: `tax_${Date.now()}`, name: "", percentage: 0, is_active: true },
      ],
    }));
  };

  const updateTax = (index, field, value) => {
    setFormData((prev) => {
      const taxes = [...prev.taxes];
      taxes[index] = {
        ...taxes[index],
        [field]: field === "percentage" ? parseFloat(value) || 0 : value,
      };
      return { ...prev, taxes };
    });
  };

  const removeTax = (index) => {
    setFormData((prev) => ({
      ...prev,
      taxes: prev.taxes.filter((_, i) => i !== index),
    }));
  };

  const calculateTotalPercentage = (taxes) => {
    return taxes.reduce((sum, tax) => sum + (tax.is_active ? tax.percentage : 0), 0);
  };

  return (
    <AdminLayout>
      <div className="animate-fade-in" data-testid="admin-taxes">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Tax Management</h1>
            <p className="text-gray-500 text-sm mt-1">Create tax groups with multiple taxes for products</p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="bg-green-500 hover:bg-green-600" data-testid="add-tax-group-btn">
                <Plus size={18} className="mr-2" />
                Add Tax Group
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg bg-white">
              <DialogHeader>
                <DialogTitle>{editingGroup ? "Edit Tax Group" : "Add New Tax Group"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Group Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Food Taxes, Beverage Taxes"
                    required
                    className="bg-white"
                    data-testid="tax-form-name"
                  />
                </div>
                <div>
                  <Label>Description (Optional)</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Description of this tax group"
                    className="bg-white"
                    data-testid="tax-form-description"
                  />
                </div>

                {/* Taxes */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center justify-between mb-4">
                    <Label className="text-base">Taxes in this Group</Label>
                    <Button type="button" variant="outline" size="sm" onClick={addTax}>
                      <Plus size={14} className="mr-1" /> Add Tax
                    </Button>
                  </div>
                  {formData.taxes.length === 0 ? (
                    <p className="text-gray-400 text-sm text-center py-4">No taxes added yet</p>
                  ) : (
                    <div className="space-y-3">
                      {formData.taxes.map((tax, index) => (
                        <div key={tax.tax_id} className="flex items-center gap-2 bg-white p-3 rounded-lg">
                          <Input
                            placeholder="Tax name (e.g., GST)"
                            value={tax.name}
                            onChange={(e) => updateTax(index, "name", e.target.value)}
                            className="flex-1 bg-white"
                          />
                          <div className="relative w-24">
                            <Input
                              type="number"
                              step="0.01"
                              placeholder="%"
                              value={tax.percentage}
                              onChange={(e) => updateTax(index, "percentage", e.target.value)}
                              className="pr-6 bg-white"
                            />
                            <Percent size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400" />
                          </div>
                          <label className="flex items-center gap-1 text-sm whitespace-nowrap">
                            <input
                              type="checkbox"
                              checked={tax.is_active}
                              onChange={(e) => updateTax(index, "is_active", e.target.checked)}
                            />
                            Active
                          </label>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeTax(index)}
                            className="text-red-500"
                          >
                            <X size={14} />
                          </Button>
                        </div>
                      ))}
                      <div className="text-right text-sm">
                        <span className="text-gray-500">Total: </span>
                        <span className="font-semibold text-gray-900">
                          {calculateTotalPercentage(formData.taxes).toFixed(2)}%
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between">
                  <Label>Active</Label>
                  <Switch
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                    data-testid="tax-form-active"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-green-500 hover:bg-green-600"
                  disabled={formLoading}
                  data-testid="tax-form-submit"
                >
                  {formLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : editingGroup ? (
                    "Update Tax Group"
                  ) : (
                    "Create Tax Group"
                  )}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filter Bar */}
        <div className="mb-6 space-y-3" data-testid="taxes-filter-bar">
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                type="text"
                placeholder="Search tax groups..."
                value={filters.name}
                onChange={(e) => setFilters({ ...filters, name: e.target.value })}
                className="pl-11 bg-white"
                data-testid="taxes-filter-name"
              />
            </div>
            <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
              <SelectTrigger className="w-36" data-testid="taxes-filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
            {(filters.name || filters.status !== "all") && (
              <Button variant="ghost" size="sm" onClick={() => setFilters({ name: "", status: "all" })} className="text-gray-500" data-testid="taxes-filter-clear">
                <X size={14} className="mr-1" /> Clear
              </Button>
            )}
          </div>
        </div>

        {/* Tax Groups Grid */}
        {(() => {
          const filteredGroups = taxGroups.filter((group) => {
            if (filters.name && !group.name.toLowerCase().includes(filters.name.toLowerCase())) return false;
            if (filters.status === "active" && !group.is_active) return false;
            if (filters.status === "inactive" && group.is_active) return false;
            return true;
          });
          return loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="skeleton h-48 w-full rounded-xl"></div>
            ))}
          </div>
        ) : filteredGroups.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-2xl border border-gray-100">
            <Percent className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No tax groups found</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredGroups.map((group) => (
              <div
                key={group.group_id}
                className="bg-white rounded-2xl border border-gray-100 p-6"
                data-testid={`tax-group-${group.group_id}`}
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{group.name}</h3>
                    {group.description && (
                      <p className="text-gray-500 text-sm mt-1">{group.description}</p>
                    )}
                    <span className={`inline-block mt-2 px-2 py-0.5 rounded-full text-xs font-medium ${
                      group.is_active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"
                    }`}>
                      {group.is_active ? "Active" : "Inactive"}
                    </span>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditDialog(group)}
                      data-testid={`edit-tax-${group.group_id}`}
                    >
                      <Pencil size={14} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => handleDelete(group.group_id)}
                      data-testid={`delete-tax-${group.group_id}`}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>

                {/* Taxes List */}
                <div className="space-y-2">
                  {group.taxes?.length > 0 ? (
                    <>
                      {group.taxes.map((tax) => (
                        <div
                          key={tax.tax_id}
                          className={`flex justify-between items-center py-2 px-3 rounded-lg ${
                            tax.is_active ? "bg-gray-50" : "bg-gray-50 opacity-50"
                          }`}
                        >
                          <span className="text-gray-700">{tax.name}</span>
                          <span className="font-medium text-gray-900">{tax.percentage}%</span>
                        </div>
                      ))}
                      <div className="border-t pt-2 mt-2 flex justify-between items-center">
                        <span className="text-gray-500 font-medium">Total Tax</span>
                        <span className="text-lg font-bold text-green-600">
                          {calculateTotalPercentage(group.taxes).toFixed(2)}%
                        </span>
                      </div>
                    </>
                  ) : (
                    <p className="text-gray-400 text-sm text-center py-4">No taxes in this group</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        );
        })()}
      </div>
    </AdminLayout>
  );
}
